import React, { useEffect, useRef, useState } from "react";
import { MenuLayout } from "../../themes/SwitchTheme";
import "./MyReceiptsPage.css";
import API from "../../api/Api";
import { PaginatedReceiptsDto } from "../../store/models/ReceiptsDto";
import { getStatusClass, getStatusDisplayText } from "./ReceiptStatusMap";
import { DateTimeFormatter, nativeJs, ZonedDateTime } from "js-joda";

export const GERMAN_DATE_FORMAT = "dd.MM.yyyy";
export const GERMAN_TIME_FORMAT = "HH:mm:ss";
const germanDateTimeFormatter = DateTimeFormatter.ofPattern(`${GERMAN_DATE_FORMAT} ${GERMAN_TIME_FORMAT}`);

export const formatUtcToLocalDateTimeInGermanFormat = (utcDateString: string) => {
  try {
    // For UTC strings, explicitly append 'Z' if it doesn't have timezone info
    const hasTimezoneIndicator = utcDateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(utcDateString);
    const utcString = hasTimezoneIndicator ? utcDateString : `${utcDateString}Z`;
    
    // Create date object from UTC string - this will automatically convert to local time
    const date = new Date(utcString);
    
    // Format the local date
    return formatGermanDateTime(date);
  } catch (error) {
    console.error('Error formatting UTC date:', error);
    return null;
  }
};

export const formatGermanDateTime = (date: Date) => {
  return ZonedDateTime.from(nativeJs(date)).format(germanDateTimeFormatter);
};


const MyReceiptsPage: React.FC = () => {
  const [receipts, setReceipts] = useState<PaginatedReceiptsDto>();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successFile, setSuccessFile] = useState<string>("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5); // Make pageSize stateful
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCloseSuccess = () => setShowSuccess(false);

  const handleUpload = () => {
    if (selectedFile) {
      const formData = new FormData();
      formData.set("file", selectedFile);
      API.uploadReceipts(formData).then(() => {
        window.location.reload();
      });
      setShowSuccess(true);
      setSuccessFile(selectedFile.name);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setTimeout(() => setShowSuccess(false), 10000);
    }
  };

  useEffect(() => {
    // Fetch receipts for the current page and pageSize
    const fetchReceipts = async () => {
      try {
        const response = await API.getReceipts(page, pageSize);
        setReceipts(response);
      } catch (error) {
        console.error("Error fetching receipts:", error);
      }
    };

    fetchReceipts();
  }, [page, pageSize]);

  return (
    <MenuLayout>
      <div className="my-receipts-container">
        <h1>Meine Bons</h1>
        <div className="subtitle">
          Laden Sie Ihren Kassenbon und sammeln Sie automatisch Bonuspunkte für jeden Einkauf!
        </div>
        <div className="upload-box">
          <div className="upload-title">Bon hochladen</div>
          <div className="upload-desc">PDF, JPG, PNG</div>
          <div className="upload-row">
            <label className="file-label" style={{ cursor: "pointer" }}>
              <span>Datei auswählen</span>
              <input
                type="file"
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".pdf,.jpg,.jpeg,.png"
              />
            </label>
            <button className="upload-btn" disabled={!selectedFile} onClick={handleUpload}>
              Hochladen
            </button>
          </div>
          {selectedFile && (
            <div className="selected-file-info">
              <span>
                {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
              </span>
              <button className="remove-file-btn" onClick={handleRemoveFile} title="Entfernen">
                ×
              </button>
            </div>
          )}
          {showSuccess && (
            <div className="upload-success-popup">
              <button className="close-success-btn" onClick={handleCloseSuccess} title="Schließen">
                ×
              </button>
              <div className="success-title">Bon erfolgreich hochgeladen</div>
              <div className="success-subtitle">{successFile} wurde hochgeladen und wird geprüft.</div>
            </div>
          )}
        </div>

        <div className="receipts-table-box">
          <div className="table-title">Meine Bons</div>
          <div className="table-subtitle">Übersicht aller hochgeladenen Kassenbons und deren Status</div>
          <table className="receipts-table">
            <thead>
              <tr>
                <th>Bild</th>
                <th>Kassenbonnummer</th>
                <th>Einreichungsdatum</th>
                <th>Status</th>
                <th>Gesamtbetrag</th>
                <th>Bonuspunkte</th>
              </tr>
            </thead>
            <tbody>
              {receipts?.receipts.map((r) => (
                <tr key={r.receiptNumber}>
                  <td>
                    <img src={r.imageUrl} alt="Beleg" style={{ maxWidth: 40, maxHeight: 40 }} />
                  </td>
                  <td>{r.externalReceiptId}</td>
                  <td>{r.submissionDate ? formatUtcToLocalDateTimeInGermanFormat(r.submissionDate) : '-'}</td>
                  <td>
                    <span className={`status-badge status-${getStatusClass(r.status)}`}>{getStatusDisplayText(r.status)}</span>
                  </td>
                  <td>{r.totalAmount}</td>
                  <td>{r.bonusPoints}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Pagination Controls with Page Size Selector */}
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 24 }}>
            {/* Pagination Navigation */}
            <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                &lt; Zurück
              </button>
              <span>
                Seite {page} von {receipts ? Math.ceil(receipts.total / pageSize) : 1}
              </span>
              <button
                onClick={() => setPage((p) => (receipts && p < Math.ceil(receipts.total / pageSize) ? p + 1 : p))}
                disabled={!receipts || page >= Math.ceil(receipts.total / pageSize)}
              >
                Weiter &gt;
              </button>
            </div>
            {/* Page Size Selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>Einträge pro Seite:</span>
              {[5, 10, 20, 50].map((size) => (
                <button
                  key={size}
                  onClick={() => { setPageSize(size); setPage(1); }}
                  style={{ fontWeight: pageSize === size ? 'bold' : 'normal' }}
                  disabled={pageSize === size}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </MenuLayout>
  );
};

export default MyReceiptsPage;
